import { IconButton, Tooltip, styled } from '@mui/material';
import { useLocales } from 'src/locales';
import Iconify from '../iconify';
import { CodeBlockSelect } from './CodeBlockSelect';
import { SyntaxHighlight } from './SyntaxHighlight';
import { lowlight } from './lowlight';

export type CodeHighlightProps = {
  language: string;
  onLanguageChange: (language: string) => void;
  copyCode: () => void;
  children: React.ReactNode;
};

export const CodeHighlight = ({
  language,
  onLanguageChange,
  copyCode,
  children,
}: CodeHighlightProps) => {
  const { translate } = useLocales();
  return (
    <SyntaxHighlight>
      <StyledCodeBlockContainer className="codeblock">
        <div className="options">
          <CodeBlockSelect
            value={language}
            onChange={(e) => {
              onLanguageChange?.(e.target.value);
            }}
          >
            <option value="">auto</option>
            {lowlight.listLanguages().map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </CodeBlockSelect>
          <Tooltip
            title={translate('shared.labels.copy_code')}
            placement="top-end"
            arrow
          >
            <StyledCopyButton size="small" color="inherit" onClick={copyCode}>
              <Iconify icon="tabler:copy" />
            </StyledCopyButton>
          </Tooltip>
        </div>
        {children}
      </StyledCodeBlockContainer>
    </SyntaxHighlight>
  );
};

const StyledCodeBlockContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  fontFamily: 'Cousine',
  padding: theme.spacing(3, 2, 0),
  margin: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  maxWidth: '100%',

  pre: {
    marginBottom: theme.spacing(3),
  },

  '.options': {
    position: 'absolute',
    top: theme.spacing(-1),
    right: theme.spacing(1),
    gap: theme.spacing(1),
    display: 'flex',
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'top'], {
      duration: theme.transitions.duration.short,
    }),
  },
  '&:hover': {
    '.options': {
      top: theme.spacing(0.5),
      opacity: 1,
      cursor: 'hand',
    },
  },
}));

const StyledCopyButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(4),
  height: theme.spacing(4),
  color: theme.palette.text.secondary,
  svg: {
    width: theme.spacing(2),
  },
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));
